import { useEffect, useRef, useState } from 'react';

export default function ConceptSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      id="sobre-mi"
      ref={sectionRef}
      className="relative bg-white py-16 lg:py-20 px-8 md:px-12 lg:px-[8%] overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center relative z-10">
        {/* Columna izquierda: Texto */}
        <div
          className={`lg:col-span-7 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-[clamp(1.8rem,3.5vw,2.6rem)] font-bold text-[#222222] leading-tight tracking-[-0.01em] mb-5">
            Soy Luna 🤍
          </h2>
          <p className="text-[17px] lg:text-[18px] text-[#3A3A3A] leading-[1.8] font-light max-w-xl mb-5">
            Tengo 24 años y soy de Valencia. Me gusta simplificar, encontrar lo que realmente funciona y compartirlo sin ruido.
          </p>
          <p className="text-[17px] lg:text-[18px] text-[#3A3A3A] leading-[1.8] font-light max-w-xl mb-5">
            Este espacio nació porque me cansé de probar mil productos que prometen y no cumplen. Aquí solo recomiendo lo que repetiría: productos que uso, que me han funcionado, y que creo que pueden ayudarte.
          </p>
          <p className="text-[17px] lg:text-[18px] text-[#3A3A3A] leading-[1.8] font-light max-w-xl mb-8">
            No soy influencer, no tengo patrocinios. Solo una obsesión con encontrar las cosas correctas — y la honestidad de decirte cuando algo no vale la pena.
          </p>

          {/* Mis criterios */}
          <div className="mb-8">
            <h3 className="text-[20px] font-semibold text-[#222222] mb-4">Mis criterios</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-[16px] text-[#3A3A3A]">
                <span className="text-[18px] flex-shrink-0">✅</span>
                <span>Lo he probado personalmente (o investigado a fondo)</span>
              </li>
              <li className="flex items-start gap-3 text-[16px] text-[#3A3A3A]">
                <span className="text-[18px] flex-shrink-0">✅</span>
                <span>Relación calidad-precio real</span>
              </li>
              <li className="flex items-start gap-3 text-[16px] text-[#3A3A3A]">
                <span className="text-[18px] flex-shrink-0">✅</span>
                <span>Sin exageraciones ni marketing</span>
              </li>
              <li className="flex items-start gap-3 text-[16px] text-[#3A3A3A]">
                <span className="text-[18px] flex-shrink-0">✅</span>
                <span>Transparencia total (incluidos enlaces de afiliado)</span>
              </li>
            </ul>
          </div>

          {/* Nota de transparencia */}
          <div className="bg-[#FAF9F7] border border-[#E8DED2] rounded-lg px-5 py-4 max-w-xl">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-[20px] text-[#8B7355]"></i>
              </div>
              <p className="text-[14px] text-[#6B6860] leading-[1.6]">
                <strong className="text-[#3A3A3A]">Transparencia:</strong> "Luna García" es un personaje digital creado con inteligencia artificial. Las recomendaciones, criterios y valores de este proyecto son reales y están cuidadosamente curados, pero la imagen y voz de Luna son generadas por IA. Más información en <a href="/aviso-legal" className="underline hover:text-[#3A3A3A] transition-colors cursor-pointer">Aviso Legal</a>.
              </p>
            </div>
          </div>
        </div>

        {/* Columna derecha: Imagen */}
        <div
          className={`lg:col-span-5 transition-all duration-700 delay-200 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="relative w-full h-[400px] lg:h-[500px]">
            <img
              src="https://static.readdy.ai/image/4c149a989a9058aed7880a7dbbd9f5af/349ee631994454e301f59c969ec634d7.jpeg"
              alt="Luna García - Personaje digital de LunaFinds"
              title="Luna García LunaFinds"
              loading="lazy"
              className="w-full h-full object-cover object-center rounded-lg shadow-sm"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
